import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Divider,
  Badge,
  Tooltip,
  Menu,
  Skeleton,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction
} from '@mui/material';
import {
  Description as FormIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Assignment as TemplateIcon,
  AssignmentTurnedIn as SubmissionIcon,
  CheckCircle as ApproveIcon,
  CheckCircle,
  Cancel as RejectIcon,
  MoreVert as MoreIcon,
  Gavel as WaiverIcon,
  Assignment as AgreementIcon,
  Rule as RulesIcon,
  Build as MaintenanceIcon,
  Report as IncidentIcon,
  Category as CategoryIcon,
  Analytics as AnalyticsIcon,
  Archive as ArchiveIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`forms-tabpanel-${index}`}
      aria-labelledby={`forms-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const FormsPage: React.FC = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [templates, setTemplates] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  
  // UI states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [submissionDialog, setSubmissionDialog] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [formBuilderDialog, setFormBuilderDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTemplates(),
        fetchSubmissions(),
        fetchPendingApprovals(),
        fetchCategories()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/forms/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/forms/submissions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    if (!user || !['admin', 'manager'].includes(user.role)) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/forms/approvals/pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingApprovals(response.data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/forms/categories`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handlePublishTemplate = async (templateId: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/forms/templates/${templateId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchTemplates();
      alert('Template published successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error publishing template');
    }
  };

  const handleCloneTemplate = async (templateId: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/forms/templates/${templateId}/clone`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchTemplates();
      alert('Template cloned successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error cloning template');
    }
  };

  const handleArchiveTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to archive this template?')) return;
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/forms/templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchTemplates();
      alert('Template archived successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error archiving template');
    }
  };

  const handleApproveSubmission = async (submissionId: string, comments?: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/forms/submissions/${submissionId}/approve`,
        { comments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApprovalDialog(false);
      fetchSubmissions();
      fetchPendingApprovals();
      alert('Submission approved successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error approving submission');
    }
  };

  const handleRejectSubmission = async (submissionId: string, reason: string, comments?: string) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/forms/submissions/${submissionId}/reject`,
        { reason, comments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApprovalDialog(false);
      fetchSubmissions();
      fetchPendingApprovals();
      alert('Submission rejected!');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error rejecting submission');
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: any = {
      'registration': <FormIcon />,
      'agreement': <AgreementIcon />,
      'waiver': <WaiverIcon />,
      'rules': <RulesIcon />,
      'maintenance': <MaintenanceIcon />,
      'incident': <IncidentIcon />
    };
    return icons[category] || <CategoryIcon />;
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'draft': 'default',
      'submitted': 'primary',
      'pending_approval': 'warning',
      'approved': 'success',
      'rejected': 'error',
      'archived': 'default'
    };
    return colors[status] || 'default';
  };

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const speedDialActions = [
    { icon: <AddIcon />, name: 'Create Template', action: () => setCreateDialog(true) },
    { icon: <UploadIcon />, name: 'Import Template', action: () => {} },
    { icon: <TemplateIcon />, name: 'Browse Gallery', action: () => {} }
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Forms & Documents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage forms, documents, and digital paperwork
        </Typography>
      </Box>

      {/* Alerts */}
      {pendingApprovals.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have {pendingApprovals.length} form submissions pending approval
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TemplateIcon color="primary" />
                <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                  Templates
                </Typography>
              </Box>
              <Typography variant="h4">
                {templates.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active forms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SubmissionIcon color="success" />
                <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                  Submissions
                </Typography>
              </Box>
              <Typography variant="h4">
                {submissions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Badge badgeContent={pendingApprovals.length} color="warning">
                  <ApproveIcon color="warning" />
                </Badge>
                <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                  Pending
                </Typography>
              </Box>
              <Typography variant="h4">
                {pendingApprovals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon color="info" />
                <Typography color="text.secondary" variant="subtitle2" sx={{ ml: 1 }}>
                  Completion Rate
                </Typography>
              </Box>
              <Typography variant="h4">
                92%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Templates" icon={<TemplateIcon />} iconPosition="start" />
          <Tab label="Submissions" icon={<SubmissionIcon />} iconPosition="start" />
          <Tab 
            label={
              <Badge badgeContent={pendingApprovals.length} color="warning">
                Approvals
              </Badge>
            }
            icon={<ApproveIcon />} 
            iconPosition="start" 
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Templates Tab */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                placeholder="Search templates..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ flexGrow: 1, maxWidth: 400 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {loading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4].map(i => (
                <Grid item xs={12} md={6} lg={4} key={i}>
                  <Skeleton variant="rectangular" height={200} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {filteredTemplates.map((template) => (
                <Grid item xs={12} md={6} lg={4} key={template._id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getCategoryIcon(template.category)}
                        </Avatar>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedTemplate(template);
                          }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Box>
                      
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip 
                          label={template.category} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={template.type} 
                          size="small" 
                        />
                        {template.versioning?.isDraft && (
                          <Chip label="Draft" size="small" color="warning" />
                        )}
                      </Stack>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          Used {template.usage?.timesUsed || 0} times
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          v{template.versioning?.version || 1}
                        </Typography>
                      </Box>
                    </CardContent>
                    
                    <CardActions>
                      <Button size="small" startIcon={<PreviewIcon />}>
                        Preview
                      </Button>
                      <Button size="small" startIcon={<EditIcon />}>
                        Edit
                      </Button>
                      {template.versioning?.isDraft && (
                        <Button 
                          size="small" 
                          color="success"
                          onClick={() => handlePublishTemplate(template._id)}
                        >
                          Publish
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Submissions Tab */}
          <List>
            {submissions.map((submission) => (
              <React.Fragment key={submission._id}>
                <ListItem
                  button
                  onClick={() => {
                    setSelectedSubmission(submission);
                    setSubmissionDialog(true);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {getCategoryIcon(submission.template?.category)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                          {submission.template?.name}
                        </Typography>
                        <Chip
                          label={submission.status}
                          size="small"
                          color={getStatusColor(submission.status)}
                          sx={{ ml: 2 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          ID: {submission.submissionId} • 
                          By: {submission.submitter.name} • 
                          {dayjs(submission.createdAt).format('MMM D, YYYY h:mm A')}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <IconButton size="small">
                        <PreviewIcon />
                      </IconButton>
                      <IconButton size="small">
                        <DownloadIcon />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Approvals Tab */}
          {pendingApprovals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CheckCircle color="success" sx={{ fontSize: 48 }} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                No pending approvals
              </Typography>
            </Box>
          ) : (
            <List>
              {pendingApprovals.map((submission) => (
                <React.Fragment key={submission._id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'warning.main' }}>
                        {getCategoryIcon(submission.template?.category)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={submission.template?.name}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            Submitted by: {submission.submitter.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(submission.createdAt).format('MMM D, YYYY h:mm A')}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<ApproveIcon />}
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setApprovalDialog(true);
                          }}
                        >
                          Review
                        </Button>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

      {/* Template Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => {
          handleCloneTemplate(selectedTemplate?._id);
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clone</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          // Export template
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={() => {
          handleArchiveTemplate(selectedTemplate?._id);
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
      </Menu>

      {/* Speed Dial for actions */}
      <SpeedDial
        ariaLabel="Form actions"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        icon={<SpeedDialIcon />}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
          />
        ))}
      </SpeedDial>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Submission</DialogTitle>
        <DialogContent>
          {selectedSubmission && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Review the submission details below and approve or reject.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                {selectedSubmission.template?.name}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Submitted by: {selectedSubmission.submitter.name} on {dayjs(selectedSubmission.createdAt).format('MMM D, YYYY h:mm A')}
              </Typography>
              
              {/* Display form data here */}
              <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  Form submission data would be displayed here...
                </Typography>
              </Paper>
              
              <TextField
                label="Comments (optional)"
                multiline
                rows={3}
                fullWidth
                sx={{ mt: 3 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button 
            color="error" 
            onClick={() => handleRejectSubmission(selectedSubmission._id, 'Incomplete information')}
          >
            Reject
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={() => handleApproveSubmission(selectedSubmission._id)}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FormsPage;